import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { Typography, Box } from '@mui/material';

import { useSocialAuth } from '@/contexts/socialAuth';

import { LANDING_ROUTES } from '@/config/routes';
import { OAuthProvider } from '@/enums/socialAuth';
import { hasFeature } from '@/utils/featureFlag';
import { getRecord } from '@/utils/storageUtils';

import { SendResetLinkDialog } from '@/components/dialogs/SendResetLinkDialog/SendResetLinkDialog';
import {
  AuthFormContent,
  AuthFormTitle,
  AuthFormDivider,
} from '@/components/forms/authForms/AuthForms.styles';
import { useStyles } from '@/components/forms/authForms/signInForm/SignInForm.styles';
import { SocialButton } from '@/components/socialButton';

import { trackEvent, ActionEvents } from '@/tracking';

import { EmailSignInForm } from './EmailSignInForm';

export const SignInForm = () => {
  const initialEmail = useMemo(() => getRecord('justLoggedOutEmail'), []);
  const signInClasses = useStyles();
  const [currentEmail, setCurrentEmail] = useState(initialEmail);

  const history = useHistory();
  const { t } = useTranslation();
  const { requestAuth } = useSocialAuth();

  const switchToSignup = () => {
    const signupPath = history.location.pathname.replace(
      LANDING_ROUTES.signin,
      LANDING_ROUTES.signup,
    );
    history.push(signupPath);
  };

  return (
    <AuthFormContent>
      <div>
        <AuthFormTitle>{t('landing.signin.title')}</AuthFormTitle>

        <Typography variant="subtitle1" align="center" className={signInClasses.subtitle}>
          {t('landing.signin.slogan')}
        </Typography>
      </div>

      <div>
        <Box display="flex" flexDirection="column" gap={2}>
          <SocialButton
            provider={OAuthProvider.google}
            onClick={() => requestAuth(OAuthProvider.google)}
          >
            {t('landing.signin.google')}
          </SocialButton>

          {hasFeature('soundcloudTrackUpload') && (
            <SocialButton
              provider={OAuthProvider.soundCloud}
              onClick={() => {
                trackEvent(ActionEvents.signInWithSoundCloudClick);
                requestAuth(OAuthProvider.soundCloud);
              }}
            >
              {t('landing.signin.soundCloud')}
            </SocialButton>
          )}
        </Box>

        <AuthFormDivider>
          <Typography variant="body2">{t('landing.welcome.divider')}</Typography>
        </AuthFormDivider>

        <EmailSignInForm initialEmail={initialEmail} onEmailChanged={setCurrentEmail} />

        <div className={signInClasses.linkButtons}>
          <Typography
            variant="caption"
            component="p"
            onClick={switchToSignup}
            color="primary"
            data-testid="forgotPassword"
          >
            {t('landing.signin.signUp')}
          </Typography>

          <SendResetLinkDialog initialEmail={currentEmail} />
        </div>
      </div>
    </AuthFormContent>
  );
};
